/**
 * VraceniPlatbyModal — refund (part of) one payment attempt through its gateway (P6-e2; admin only, like the API's `/platby/*`).
 * The dialog sends the amount with an idempotency key, so a double click or a retry after a lost answer returns the first outcome
 * instead of refunding twice; a request the server REFUSED (409) is over, so the next one gets a new key. A refund the gateway has not
 * confirmed yet is told as such — it keeps reserving its amount. Nothing can dismiss the dialog while the request is in flight.
 */

import { useState } from 'react';
import { Button } from '@/components/Button';
import { Field, Fields } from '@/components/Field';
import { Modal, DialogCrown } from '@/components/Modal';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import { useVratitPlatbuMutation, type Platba } from './api';
import { POZNAMKA_MAX, castkaChyba, metodaNazev, parseCastka, providerNazev } from './platby-helpers';

const novyKlic = () => `ui-${crypto.randomUUID().replace(/-/g, '')}`;

/**
 * The key of the request last sent for each attempt, kept outside the dialog: a lost answer followed by closing and reopening the dialog
 * is still the SAME refund. Dropped once the server answered (done, or refused).
 */
const odeslano = new Map<number, { podpis: string; klic: string }>();

interface VraceniPlatbyModalProps {
  platba: Platba;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VraceniPlatbyModal({ platba, open, onOpenChange }: VraceniPlatbyModalProps) {
  const vratit = useVratitPlatbuMutation(platba.id);
  const [castka, setCastka] = useState(String(platba.lze_vratit));
  const [duvod, setDuvod] = useState('');
  const busy = vratit.isPending;
  const chyba = castkaChyba(castka, platba.lze_vratit);
  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };

  const submit = () => {
    if (busy) return;
    const n = parseCastka(castka);
    if (chyba || n == null) return void toast.error(chyba ?? 'Zadejte částku v celých korunách.');
    const podpis = `${n}|${duvod.trim()}`;
    // one key per distinct request (amount + reason): the same request repeated is the same refund, a changed one is a new refund
    let zaznam = odeslano.get(platba.id);
    if (zaznam?.podpis !== podpis) {
      zaznam = { podpis, klic: novyKlic() };
      odeslano.set(platba.id, zaznam);
    }
    vratit.mutate(
      { castka: n, duvod: duvod.trim(), klic: zaznam.klic },
      {
        onSuccess: (res) => {
          odeslano.delete(platba.id);
          if (res.stav === 'ceka') toast.warn('Vrácení je odesláno, ale brána ho ještě nepotvrdila. Částka zůstává rezervovaná.');
          else toast.ok(`Vráceno ${formatInt(n)} Kč.`);
          onOpenChange(false);
        },
        onError: (err) => {
          // a refusal ends that request: the next attempt (maybe with the same amount) is a new one
          if ((err as ApiError).status === 409) odeslano.delete(platba.id);
          toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se vrátit platbu.'}`);
        },
      },
    );
  };

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={560}>
      <DialogCrown
        eyebrow="Vrácení platby"
        title={
          <>
            Vrátit <em>platbu № {platba.id}</em>
          </>
        }
        sub={`${providerNazev(platba.provider)} · ${metodaNazev(platba.metoda)} · přijato ${formatInt(platba.prijato)} Kč, vráceno ${formatInt(platba.vraceno)} Kč.`}
      />
      <Modal.Body className="px-8 py-5">
        <form
          id="vraceni-platby-form"
          data-testid="vraceni-platby-form"
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <p className="text-[12.5px] leading-relaxed text-ink-2">
            Peníze se vrátí přes platební bránu na zdroj platby. Vrácení nelze vzít zpět.
          </p>
          <Fields cols={1}>
            <Field
              label="Částka"
              required
              state={chyba && castka.trim() !== '' ? 'error' : undefined}
              hint={chyba && castka.trim() !== '' ? chyba : `Nejvýše ${formatInt(platba.lze_vratit)} Kč.`}
              hintTone={chyba && castka.trim() !== '' ? 'error' : 'neutral'}
            >
              <TextInput inputMode="numeric" value={castka} suffix="Kč" disabled={busy} data-testid="vraceni-castka" onChange={(e) => setCastka(e.currentTarget.value)} />
            </Field>
            <Field label="Důvod" optional="volitelné" hint="Uloží se do historie platby.">
              <TextInput value={duvod} maxLength={POZNAMKA_MAX} disabled={busy} data-testid="vraceni-duvod" onChange={(e) => setDuvod(e.currentTarget.value)} />
            </Field>
          </Fields>
        </form>
      </Modal.Body>
      <Modal.Foot
        actions={
          <>
            <Button variant="secondary" onClick={() => guarded(false)} disabled={busy}>
              Zrušit
            </Button>
            <Button variant="ink" type="submit" form="vraceni-platby-form" disabled={busy || chyba != null} data-testid="vraceni-potvrdit">
              {busy ? 'Vracím…' : chyba ? 'Vrátit' : `Vrátit ${formatInt(parseCastka(castka))} Kč`}
            </Button>
          </>
        }
      />
    </Modal>
  );
}
